import { useEffect, useState } from "react";
import { motion } from "framer-motion";

import {
  Wallet,
  ShoppingCart,
  IndianRupee,
  Users,
  MapPin,
  Clock,
  CheckCircle,
  TrendingUp,
  CalendarDays,
  RefreshCw,
  Receipt,
  UserRound,
  BarChart3,
} from "lucide-react";

import api from "../constant/api";
import { toast } from "react-toastify";
import { useMenuPermission } from "../hooks/useMenuPermission";

const MENU_ID = 23;

// ============================================================
// HELPERS
// ============================================================

const money = (value) => {
  return Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
};

const formatDate = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ============================================================
// STATUS COLOR
// ============================================================

const getOrderStatusClass = (status) => {
  const value = String(status || "").toLowerCase();

  if (
    value === "completed" ||
    value === "confirmed" ||
    value === "delivered"
  ) {
    return "bg-green-100 text-green-700";
  }

  if (
    value === "cancelled" ||
    value === "rejected"
  ) {
    return "bg-red-100 text-red-700";
  }

  if (
    value === "pending" ||
    value === "processing"
  ) {
    return "bg-amber-100 text-amber-700";
  }

  return "bg-blue-100 text-blue-700";
};

// ============================================================
// COMPONENT
// ============================================================

export default function SalesmanDashboard() {
  const {
    loading: permissionLoading,
  } = useMenuPermission(MENU_ID);

  // ==========================================================
  // USER
  // ==========================================================

  const storedUser = JSON.parse(
    localStorage.getItem("user") || "{}"
  );

  const user_id =
    storedUser.user_id ??
    storedUser.admin_id ??
    localStorage.getItem("user_id");

  const role =
    storedUser.role ||
    localStorage.getItem("userRole") ||
    "";

  const isAdmin =
    role === "admin" ||
    role === "superadmin";

  // ==========================================================
  // STATES
  // ==========================================================

  const [dashboard, setDashboard] = useState(null);

  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [salesmanSearch, setSalesmanSearch] =
    useState("");

  // ==========================================================
  // LOAD DASHBOARD
  // ==========================================================

  const loadDashboard = async (
    showRefresh = false
  ) => {
    if (
      user_id === undefined ||
      user_id === null ||
      user_id === ""
    ) {
      toast.error("User ID not found");

      setLoading(false);

      return;
    }

    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const res = await api.get(
        `/Salesman/getDashboard?user_id=${encodeURIComponent(
          user_id
        )}`
      );

      console.log(
        "SALESMAN DASHBOARD RESPONSE:",
        res.data
      );

      if (res.data?.success) {
        setDashboard(res.data);
      } else {
        setDashboard(null);

        toast.error(
          res.data?.msg ||
            "Failed to load dashboard"
        );
      }
    } catch (error) {
      console.log(
        "Dashboard Error:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.msg ||
          "Failed to load dashboard"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    if (user_id) {
      loadDashboard();
    }
  }, [user_id]);

  // ==========================================================
  // PERMISSION LOADING
  // ==========================================================

  if (permissionLoading) {
    return (
      <div className="p-6 text-gray-500">
        Checking permissions...
      </div>
    );
  }

  // ==========================================================
  // DASHBOARD LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="animate-pulse p-1">

        <div className="h-8 w-56 bg-gray-200 rounded mb-2" />

        <div className="h-4 w-72 bg-gray-200 rounded mb-6" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

          {Array.from({ length: 8 }).map(
            (_, index) => (
              <div
                key={index}
                className="
                  bg-gray-200
                  h-32
                  rounded-2xl
                "
              />
            )
          )}

        </div>

      </div>
    );
  }

  // ==========================================================
  // EMPTY
  // ==========================================================

  if (!dashboard) {
    return (
      <div
        className="
          bg-white
          rounded-2xl
          border
          p-10
          shadow-sm
        "
      >
        <p className="text-gray-500 mb-4">
          Dashboard data not available.
        </p>

        <button
          onClick={() =>
            loadDashboard(true)
          }
          className="
            bg-blue-600
            hover:bg-blue-700
            text-white
            px-5
            py-2.5
            rounded-xl
            inline-flex
            items-center
            gap-2
          "
        >
          <RefreshCw size={16} />

          Retry
        </button>
      </div>
    );
  }

  // ==========================================================
  // DATA
  // ==========================================================

  const summary =
    dashboard.summary || {};

  const user =
    dashboard.user || storedUser;

  const chart =
    Array.isArray(
      dashboard.sales_chart
    )
      ? dashboard.sales_chart
      : [];

  const recentOrders =
    Array.isArray(
      dashboard.recent_orders
    )
      ? dashboard.recent_orders
      : [];

  const recentVisits =
    Array.isArray(
      dashboard.recent_visits
    )
      ? dashboard.recent_visits
      : [];

  // ==========================================================
  // SALESMEN
  // ADMIN / SUPERADMIN
  // ==========================================================

  const salesmen =
    Array.isArray(
      dashboard.salesmen
    )
      ? dashboard.salesmen
      : [];

  const filteredSalesmen =
    salesmen.filter((salesman) => {
      const search =
        salesmanSearch
          .trim()
          .toLowerCase();

      if (!search) {
        return true;
      }

      return (
        String(
          salesman.name || ""
        )
          .toLowerCase()
          .includes(search) ||
        String(
          salesman.employee_code || ""
        )
          .toLowerCase()
          .includes(search) ||
        String(
          salesman.email || ""
        )
          .toLowerCase()
          .includes(search) ||
        String(
          salesman.phone || ""
        )
          .toLowerCase()
          .includes(search)
      );
    });

  // ==========================================================
  // CHART MAX
  // ==========================================================

  const maxSales = Math.max(
    ...chart.map((item) =>
      Number(item.sales || 0)
    ),
    1
  );

  // ==========================================================
  // STAT CARD
  // ==========================================================

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    iconBg,
    iconColor,
  }) => {
    return (
      <motion.div
        initial={{
          opacity: 0,
          y: 15,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        className="
          bg-white
          rounded-2xl
          border
          border-gray-100
          shadow-sm
          p-4
          hover:shadow-md
          transition
        "
      >
        <div
          className="
            flex
            items-start
            justify-between
          "
        >
          <div>
            <p
              className="
                text-xs
                md:text-sm
                text-gray-500
                font-medium
              "
            >
              {title}
            </p>

            <h3
              className="
                text-xl
                md:text-2xl
                font-bold
                text-gray-800
                mt-1
              "
            >
              {value}
            </h3>

            {subtitle && (
              <p
                className="
                  text-[11px]
                  md:text-xs
                  text-gray-400
                  mt-1
                "
              >
                {subtitle}
              </p>
            )}
          </div>

          <div
            className={`
              w-10
              h-10
              md:w-11
              md:h-11
              rounded-xl
              flex
              items-center
              justify-center
              ${iconBg}
            `}
          >
            <Icon
              size={20}
              className={iconColor}
            />
          </div>
        </div>
      </motion.div>
    );
  };

  // ==========================================================
  // RETURN
  // ==========================================================

  return (
    <div className="space-y-5">

      {/* ====================================================
          HEADER
      ==================================================== */}

      <div
        className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-3
        "
      >
        <div>
          <h1
            className="
              text-2xl
              md:text-3xl
              font-bold
              text-gray-800
            "
          >
            Salesman Dashboard
          </h1>

          <p
            className="
              text-sm
              text-gray-500
              mt-1
            "
          >
            Welcome back,{" "}
            <span
              className="
                font-semibold
                text-gray-700
              "
            >
              {user.name ||
                "Salesman"}
            </span>
          </p>
        </div>

        <button
          onClick={() =>
            loadDashboard(true)
          }
          disabled={refreshing}
          className="
            self-start
            sm:self-auto
            flex
            items-center
            gap-2
            bg-gray-900
            hover:bg-gray-800
            text-white
            px-4
            py-2.5
            rounded-xl
            text-sm
            font-medium
            disabled:opacity-50
          "
        >
          <RefreshCw
            size={16}
            className={
              refreshing
                ? "animate-spin"
                : ""
            }
          />

          {refreshing
            ? "Refreshing..."
            : "Refresh"}
        </button>
      </div>

      {/* ====================================================
          USER INFO
      ==================================================== */}

      <div
        className="
          bg-gradient-to-r
          from-blue-600
          to-indigo-600
          text-white
          rounded-2xl
          p-4
          md:p-5
          shadow-lg
        "
      >
        <div
          className="
            flex
            flex-col
            md:flex-row
            md:items-center
            md:justify-between
            gap-3
          "
        >
          <div>
            <p
              className="
                text-xs
                text-white/70
              "
            >
              {isAdmin
                ? "Administrator"
                : "Salesman"}
            </p>

            <h2
              className="
                text-xl
                font-bold
              "
            >
              {user.name ||
                "Salesman"}
            </h2>

            {user.employee_code && (
              <p
                className="
                  text-xs
                  text-white/70
                  mt-1
                "
              >
                Employee Code:{" "}
                {user.employee_code}
              </p>
            )}
          </div>

          <div
            className="
              text-left
              md:text-right
            "
          >
            <p
              className="
                text-xs
                text-white/70
              "
            >
              Today's Sales
            </p>

            <p
              className="
                text-2xl
                md:text-3xl
                font-bold
              "
            >
              ₹
              {money(
                summary.today_sales
              )}
            </p>
          </div>
        </div>
      </div>

      {/* ====================================================
          SALES PERFORMANCE
      ==================================================== */}

      <div>

        <div
          className="
            flex
            items-center
            gap-2
            mb-3
          "
        >
          <TrendingUp
            size={18}
            className="text-blue-600"
          />

          <h2
            className="
              font-bold
              text-gray-800
            "
          >
            Sales Performance
          </h2>
        </div>

        <div
          className="
            grid
            grid-cols-2
            lg:grid-cols-4
            gap-3
            md:gap-4
          "
        >

          <StatCard
            title="Today's Sales"
            value={`₹${money(
              summary.today_sales
            )}`}
            subtitle={`${summary.today_orders || 0} orders today`}
            icon={IndianRupee}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
          />

          <StatCard
            title="Monthly Sales"
            value={`₹${money(
              summary.monthly_sales
            )}`}
            subtitle={`${summary.monthly_orders || 0} orders this month`}
            icon={CalendarDays}
            iconBg="bg-indigo-100"
            iconColor="text-indigo-600"
          />

          <StatCard
            title="Yearly Sales"
            value={`₹${money(
              summary.yearly_sales
            )}`}
            subtitle="Current calendar year"
            icon={TrendingUp}
            iconBg="bg-green-100"
            iconColor="text-green-600"
          />

          <StatCard
            title="Total Sales"
            value={`₹${money(
              summary.total_sales
            )}`}
            subtitle={`${summary.total_orders || 0} total orders`}
            icon={Wallet}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
          />

        </div>
      </div>

      {/* ====================================================
          ADMIN / SUPERADMIN SALESMAN LIST
      ==================================================== */}

      {isAdmin && (
        <div
          className="
            bg-white
            rounded-2xl
            border
            border-gray-100
            shadow-sm
            overflow-hidden
          "
        >

          <div
            className="
              p-4
              md:p-5
              border-b
              border-gray-100
            "
          >

            <div
              className="
                flex
                flex-col
                md:flex-row
                md:items-center
                md:justify-between
                gap-3
              "
            >

              <div
                className="
                  flex
                  items-center
                  gap-2
                "
              >

                <Users
                  size={20}
                  className="text-blue-600"
                />

                <div>

                  <h2
                    className="
                      font-bold
                      text-gray-800
                    "
                  >
                    Salesman List
                  </h2>

                  <p
                    className="
                      text-xs
                      text-gray-400
                      mt-0.5
                    "
                  >
                    All salesmen performance
                  </p>

                </div>

                <span
                  className="
                    ml-1
                    text-xs
                    bg-blue-50
                    text-blue-600
                    px-2
                    py-1
                    rounded-full
                    font-semibold
                  "
                >
                  {salesmen.length}
                </span>

              </div>

              <div className="w-full md:w-72">

                <input
                  type="text"
                  value={salesmanSearch}
                  onChange={(e) =>
                    setSalesmanSearch(
                      e.target.value
                    )
                  }
                  placeholder="Search salesman..."
                  className="
                    border
                    border-gray-200
                    w-full
                    px-3
                    py-2.5
                    rounded-xl
                    text-sm
                    outline-none
                    focus:ring-2
                    focus:ring-blue-500
                  "
                />

              </div>

            </div>

          </div>

          {/* DESKTOP SALESMAN TABLE */}

          <div
            className="
              hidden
              md:block
              overflow-x-auto
            "
          >

            <table
              className="
                min-w-full
                text-sm
              "
            >

              <thead
                className="
                  bg-gray-50
                  text-gray-500
                "
              >

                <tr>

                  <th
                    className="
                      px-5
                      py-3
                      text-left
                    "
                  >
                    Salesman
                  </th>

                  <th
                    className="
                      px-5
                      py-3
                      text-left
                    "
                  >
                    Employee Code
                  </th>

                  <th
                    className="
                      px-5
                      py-3
                      text-center
                    "
                  >
                    Today Orders
                  </th>

                  <th
                    className="
                      px-5
                      py-3
                      text-right
                    "
                  >
                    Today Sales
                  </th>

                  <th
                    className="
                      px-5
                      py-3
                      text-center
                    "
                  >
                    Month Orders
                  </th>

                  <th
                    className="
                      px-5
                      py-3
                      text-right
                    "
                  >
                    Monthly Sales
                  </th>

                  <th
                    className="
                      px-5
                      py-3
                      text-right
                    "
                  >
                    Total Sales
                  </th>

                  <th
                    className="
                      px-5
                      py-3
                      text-center
                    "
                  >
                    Visits
                  </th>

                </tr>

              </thead>

              <tbody>

                {filteredSalesmen.map(
                  (salesman, index) => (

                    <motion.tr
                      key={
                        salesman.user_id ||
                        salesman.admin_id ||
                        salesman.id ||
                        index
                      }
                      initial={{
                        opacity: 0,
                        y: 8,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      className="
                        border-b
                        last:border-0
                        hover:bg-gray-50
                      "
                    >

                      {/* SALESMAN */}

                      <td className="px-5 py-4">

                        <div
                          className="
                            flex
                            items-center
                            gap-3
                          "
                        >

                          <div
                            className="
                              w-10
                              h-10
                              rounded-full
                              bg-blue-100
                              text-blue-600
                              flex
                              items-center
                              justify-center
                              font-bold
                            "
                          >
                            {(salesman.name ||
                              "S")
                              .charAt(0)
                              .toUpperCase()}
                          </div>

                          <div>

                            <p
                              className="
                                font-semibold
                                text-gray-800
                              "
                            >
                              {salesman.name ||
                                "Unknown"}
                            </p>

                            <p
                              className="
                                text-xs
                                text-gray-400
                              "
                            >
                              {salesman.phone ||
                                salesman.email ||
                                "—"}
                            </p>

                          </div>

                        </div>

                      </td>

                      {/* EMPLOYEE CODE */}

                      <td
                        className="
                          px-5
                          py-4
                          text-gray-600
                        "
                      >
                        {salesman.employee_code ||
                          "—"}
                      </td>

                      {/* TODAY ORDERS */}

                      <td
                        className="
                          px-5
                          py-4
                          text-center
                          font-semibold
                        "
                      >
                        {salesman.today_orders ||
                          0}
                      </td>

                      {/* TODAY SALES */}

                      <td
                        className="
                          px-5
                          py-4
                          text-right
                          font-bold
                          text-blue-600
                        "
                      >
                        ₹
                        {money(
                          salesman.today_sales
                        )}
                      </td>

                      {/* MONTH ORDERS */}

                      <td
                        className="
                          px-5
                          py-4
                          text-center
                          font-semibold
                        "
                      >
                        {salesman.monthly_orders ||
                          0}
                      </td>

                      {/* MONTH SALES */}

                      <td
                        className="
                          px-5
                          py-4
                          text-right
                          font-bold
                          text-indigo-600
                        "
                      >
                        ₹
                        {money(
                          salesman.monthly_sales
                        )}
                      </td>

                      {/* TOTAL SALES */}

                      <td
                        className="
                          px-5
                          py-4
                          text-right
                          font-bold
                          text-green-600
                        "
                      >
                        ₹
                        {money(
                          salesman.total_sales
                        )}
                      </td>

                      {/* VISITS */}

                      <td
                        className="
                          px-5
                          py-4
                          text-center
                        "
                      >
                        <span
                          className="
                            inline-flex
                            items-center
                            gap-1
                            bg-orange-50
                            text-orange-600
                            px-2
                            py-1
                            rounded-full
                            text-xs
                            font-semibold
                          "
                        >
                          <MapPin size={12} />

                          {salesman.total_visits ||
                            0}
                        </span>
                      </td>

                    </motion.tr>

                  )
                )}

              </tbody>

            </table>

            {filteredSalesmen.length === 0 && (
              <div
                className="
                  text-center
                  py-12
                  text-gray-400
                "
              >
                <Users
                  className="
                    mx-auto
                    mb-2
                  "
                  size={30}
                />

                No salesmen found.
              </div>
            )}

          </div>

          {/* MOBILE SALESMAN CARDS */}

          <div
            className="
              md:hidden
              divide-y
            "
          >

            {filteredSalesmen.map(
              (salesman, index) => (

                <motion.div
                  key={
                    salesman.user_id ||
                    salesman.admin_id ||
                    salesman.id ||
                    index
                  }
                  initial={{
                    opacity: 0,
                    y: 10,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  className="p-4"
                >

                  <div
                    className="
                      flex
                      items-start
                      justify-between
                      gap-3
                    "
                  >

                    <div
                      className="
                        flex
                        items-center
                        gap-3
                      "
                    >

                      <div
                        className="
                          w-11
                          h-11
                          rounded-full
                          bg-blue-100
                          text-blue-600
                          flex
                          items-center
                          justify-center
                          font-bold
                          shrink-0
                        "
                      >
                        {(salesman.name ||
                          "S")
                          .charAt(0)
                          .toUpperCase()}
                      </div>

                      <div
                        className="
                          min-w-0
                        "
                      >

                        <p
                          className="
                            font-bold
                            text-gray-800
                            truncate
                          "
                        >
                          {salesman.name ||
                            "Unknown"}
                        </p>

                        <p
                          className="
                            text-xs
                            text-gray-400
                          "
                        >
                          {salesman.employee_code ||
                            "No employee code"}
                        </p>

                        <p
                          className="
                            text-xs
                            text-gray-400
                            truncate
                          "
                        >
                          {salesman.phone ||
                            salesman.email ||
                            ""}
                        </p>

                      </div>

                    </div>

                    <div
                      className="
                        text-right
                        shrink-0
                      "
                    >

                      <p
                        className="
                          text-xs
                          text-gray-400
                        "
                      >
                        Monthly
                      </p>

                      <p
                        className="
                          font-bold
                          text-blue-600
                        "
                      >
                        ₹
                        {money(
                          salesman.monthly_sales
                        )}
                      </p>

                    </div>

                  </div>

                  <div
                    className="
                      grid
                      grid-cols-2
                      gap-2
                      mt-4
                    "
                  >

                    <div
                      className="
                        bg-blue-50
                        rounded-xl
                        p-3
                      "
                    >

                      <p
                        className="
                          text-[11px]
                          text-blue-500
                        "
                      >
                        Today Sales
                      </p>

                      <p
                        className="
                          font-bold
                          text-blue-700
                          mt-1
                        "
                      >
                        ₹
                        {money(
                          salesman.today_sales
                        )}
                      </p>

                    </div>

                    <div
                      className="
                        bg-indigo-50
                        rounded-xl
                        p-3
                      "
                    >

                      <p
                        className="
                          text-[11px]
                          text-indigo-500
                        "
                      >
                        Month Sales
                      </p>

                      <p
                        className="
                          font-bold
                          text-indigo-700
                          mt-1
                        "
                      >
                        ₹
                        {money(
                          salesman.monthly_sales
                        )}
                      </p>

                    </div>

                    <div
                      className="
                        bg-green-50
                        rounded-xl
                        p-3
                      "
                    >

                      <p
                        className="
                          text-[11px]
                          text-green-600
                        "
                      >
                        Total Sales
                      </p>

                      <p
                        className="
                          font-bold
                          text-green-700
                          mt-1
                        "
                      >
                        ₹
                        {money(
                          salesman.total_sales
                        )}
                      </p>

                    </div>

                    <div
                      className="
                        bg-orange-50
                        rounded-xl
                        p-3
                      "
                    >

                      <p
                        className="
                          text-[11px]
                          text-orange-600
                        "
                      >
                        Visits
                      </p>

                      <p
                        className="
                          font-bold
                          text-orange-700
                          mt-1
                        "
                      >
                        {salesman.total_visits ||
                          0}
                      </p>

                    </div>

                  </div>

                  <div
                    className="
                      flex
                      justify-between
                      mt-3
                      text-xs
                      text-gray-400
                    "
                  >

                    <span>
                      Today Orders:{" "}
                      <b className="text-gray-700">
                        {salesman.today_orders ||
                          0}
                      </b>
                    </span>

                    <span>
                      Month Orders:{" "}
                      <b className="text-gray-700">
                        {salesman.monthly_orders ||
                          0}
                      </b>
                    </span>

                  </div>

                </motion.div>

              )
            )}

            {filteredSalesmen.length === 0 && (
              <div
                className="
                  text-center
                  py-12
                  text-gray-400
                "
              >
                No salesmen found.
              </div>
            )}

          </div>

        </div>
      )}

      {/* ====================================================
          SALES CHART
      ==================================================== */}

      <div
        className="
          bg-white
          rounded-2xl
          border
          border-gray-100
          shadow-sm
          p-4
          md:p-5
        "
      >

        <div
          className="
            flex
            items-center
            justify-between
            mb-5
          "
        >

          <div>

            <h2
              className="
                font-bold
                text-gray-800
              "
            >
              Sales - Last 7 Days
            </h2>

            <p
              className="
                text-xs
                text-gray-400
                mt-1
              "
            >
              Daily sales performance
            </p>

          </div>

          <TrendingUp
            size={20}
            className="text-blue-600"
          />

        </div>

        {chart.length > 0 ? (
          <div
            className="
              h-56
              flex
              items-end
              gap-2
              md:gap-4
              overflow-x-auto
            "
          >

            {chart.map((item) => {

              const sales =
                Number(
                  item.sales || 0
                );

              const height =
                Math.max(
                  (sales /
                    maxSales) *
                    100,
                  sales > 0
                    ? 5
                    : 2
                );

              return (
                <div
                  key={item.date}
                  className="
                    flex-1
                    min-w-[55px]
                    h-full
                    flex
                    flex-col
                    justify-end
                    items-center
                  "
                >

                  <div
                    className="
                      text-[10px]
                      text-gray-500
                      mb-1
                      whitespace-nowrap
                    "
                  >
                    ₹{money(sales)}
                  </div>

                  <motion.div
                    initial={{
                      height: 0,
                    }}
                    animate={{
                      height:
                        `${height}%`,
                    }}
                    transition={{
                      duration: 0.6,
                    }}
                    className="
                      w-full
                      max-w-[55px]
                      bg-blue-500
                      rounded-t-xl
                      min-h-[3px]
                    "
                  />

                  <div
                    className="
                      text-[10px]
                      text-gray-400
                      mt-2
                      whitespace-nowrap
                    "
                  >
                    {new Date(
                      item.date
                    ).toLocaleDateString(
                      "en-IN",
                      {
                        day: "2-digit",
                        month: "short",
                      }
                    )}
                  </div>

                </div>
              );
            })}

          </div>
        ) : (
          <div
            className="
              h-56
              flex
              items-center
              justify-center
              text-gray-400
              text-sm
            "
          >
            No sales data available.
          </div>
        )}

      </div>

      {/* ====================================================
          ORDER + VISIT
      ==================================================== */}

      <div
        className="
          grid
          grid-cols-1
          md:grid-cols-2
          gap-4
        "
      >

        {/* ORDERS */}

        <div
          className="
            bg-white
            rounded-2xl
            border
            border-gray-100
            shadow-sm
            p-5
          "
        >

          <div
            className="
              flex
              items-center
              justify-between
              mb-4
            "
          >

            <div
              className="
                flex
                items-center
                gap-2
              "
            >

              <ShoppingCart
                size={19}
                className="text-blue-600"
              />

              <h2
                className="
                  font-bold
                  text-gray-800
                "
              >
                Orders
              </h2>

            </div>

            <span
              className="
                text-xs
                bg-blue-50
                text-blue-600
                px-2
                py-1
                rounded-full
                font-semibold
              "
            >
              {summary.total_orders ||
                0}
            </span>

          </div>

          <div
            className="
              grid
              grid-cols-3
              gap-2
            "
          >

            <div
              className="
                bg-gray-50
                rounded-xl
                p-3
              "
            >
              <p className="text-xs text-gray-400">
                Today
              </p>

              <p
                className="
                  text-xl
                  font-bold
                  text-gray-800
                  mt-1
                "
              >
                {summary.today_orders ||
                  0}
              </p>
            </div>

            <div
              className="
                bg-gray-50
                rounded-xl
                p-3
              "
            >
              <p className="text-xs text-gray-400">
                Month
              </p>

              <p
                className="
                  text-xl
                  font-bold
                  text-gray-800
                  mt-1
                "
              >
                {summary.monthly_orders ||
                  0}
              </p>
            </div>

            <div
              className="
                bg-gray-50
                rounded-xl
                p-3
              "
            >
              <p className="text-xs text-gray-400">
                Total
              </p>

              <p
                className="
                  text-xl
                  font-bold
                  text-gray-800
                  mt-1
                "
              >
                {summary.total_orders ||
                  0}
              </p>
            </div>

          </div>

        </div>

        {/* VISITS */}

        <div
          className="
            bg-white
            rounded-2xl
            border
            border-gray-100
            shadow-sm
            p-5
          "
        >

          <div
            className="
              flex
              items-center
              justify-between
              mb-4
            "
          >

            <div
              className="
                flex
                items-center
                gap-2
              "
            >

              <MapPin
                size={19}
                className="text-orange-500"
              />

              <h2
                className="
                  font-bold
                  text-gray-800
                "
              >
                Customer Visits
              </h2>

            </div>

            <span
              className="
                text-xs
                bg-orange-50
                text-orange-600
                px-2
                py-1
                rounded-full
                font-semibold
              "
            >
              {summary.total_visits ||
                0}
            </span>

          </div>

          <div
            className="
              grid
              grid-cols-3
              gap-2
            "
          >

            <div
              className="
                bg-gray-50
                rounded-xl
                p-3
              "
            >
              <p className="text-xs text-gray-400">
                Today
              </p>

              <p
                className="
                  text-xl
                  font-bold
                  text-gray-800
                  mt-1
                "
              >
                {summary.today_visits ||
                  0}
              </p>
            </div>

            <div
              className="
                bg-gray-50
                rounded-xl
                p-3
              "
            >
              <p className="text-xs text-gray-400">
                Done
              </p>

              <p
                className="
                  text-xl
                  font-bold
                  text-green-600
                  mt-1
                "
              >
                {summary.completed_visits ||
                  0}
              </p>
            </div>

            <div
              className="
                bg-gray-50
                rounded-xl
                p-3
              "
            >
              <p className="text-xs text-gray-400">
                Active
              </p>

              <p
                className="
                  text-xl
                  font-bold
                  text-orange-500
                  mt-1
                "
              >
                {summary.active_visits ||
                  0}
              </p>
            </div>

          </div>

        </div>

      </div>

      {/* ====================================================
          EXPENSES
      ==================================================== */}

      <div
        className="
          bg-white
          rounded-2xl
          border
          border-gray-100
          shadow-sm
          p-4
          md:p-5
        "
      >

        <div
          className="
            flex
            items-center
            justify-between
            mb-4
          "
        >

          <div
            className="
              flex
              items-center
              gap-2
            "
          >

            <Receipt
              size={19}
              className="text-purple-600"
            />

            <h2
              className="
                font-bold
                text-gray-800
              "
            >
              My Expenses
            </h2>

          </div>

          <p
            className="
              text-lg
              font-bold
              text-gray-800
            "
          >
            ₹
            {money(
              summary.monthly_expenses
            )}
          </p>

        </div>

        <div
          className="
            grid
            grid-cols-2
            md:grid-cols-5
            gap-3
          "
        >

          <div
            className="
              bg-gray-50
              rounded-xl
              p-3
            "
          >
            <p className="text-xs text-gray-400">
              Today
            </p>

            <p
              className="
                font-bold
                text-gray-800
                mt-1
              "
            >
              ₹
              {money(
                summary.today_expenses
              )}
            </p>
          </div>

          <div
            className="
              bg-gray-50
              rounded-xl
              p-3
            "
          >
            <p className="text-xs text-gray-400">
              Month
            </p>

            <p
              className="
                font-bold
                text-gray-800
                mt-1
              "
            >
              ₹
              {money(
                summary.monthly_expenses
              )}
            </p>
          </div>

          <div
            className="
              bg-green-50
              rounded-xl
              p-3
            "
          >
            <p className="text-xs text-green-600">
              Approved
            </p>

            <p
              className="
                font-bold
                text-green-700
                mt-1
              "
            >
              ₹
              {money(
                summary.approved_expenses
              )}
            </p>
          </div>

          <div
            className="
              bg-amber-50
              rounded-xl
              p-3
            "
          >
            <p className="text-xs text-amber-600">
              Pending
            </p>

            <p
              className="
                font-bold
                text-amber-700
                mt-1
              "
            >
              ₹
              {money(
                summary.pending_expenses
              )}
            </p>
          </div>

          <div
            className="
              bg-red-50
              rounded-xl
              p-3
            "
          >
            <p className="text-xs text-red-600">
              Rejected
            </p>

            <p
              className="
                font-bold
                text-red-700
                mt-1
              "
            >
              ₹
              {money(
                summary.rejected_expenses
              )}
            </p>
          </div>

        </div>

      </div>

      {/* ====================================================
          RECENT ORDERS
      ==================================================== */}

      <div
        className="
          bg-white
          rounded-2xl
          border
          border-gray-100
          shadow-sm
          overflow-hidden
        "
      >

        <div
          className="
            p-4
            md:p-5
            border-b
            border-gray-100
            flex
            items-center
            justify-between
          "
        >

          <div
            className="
              flex
              items-center
              gap-2
            "
          >

            <ShoppingCart
              size={18}
              className="text-blue-600"
            />

            <h2
              className="
                font-bold
                text-gray-800
              "
            >
              Recent Orders
            </h2>

          </div>

          <span
            className="
              text-xs
              text-gray-400
            "
          >
            Last 5
          </span>

        </div>

        {/* DESKTOP */}

        <div
          className="
            hidden
            md:block
            overflow-x-auto
          "
        >

          <table
            className="
              min-w-full
              text-sm
            "
          >

            <thead
              className="
                bg-gray-50
                text-gray-500
              "
            >

              <tr>

                <th className="px-5 py-3 text-left">
                  Order
                </th>

                <th className="px-5 py-3 text-left">
                  Customer
                </th>

                <th className="px-5 py-3 text-right">
                  Amount
                </th>

                <th className="px-5 py-3 text-center">
                  Status
                </th>

                <th className="px-5 py-3 text-right">
                  Date
                </th>

              </tr>

            </thead>

            <tbody>

              {recentOrders.map(
                (order) => (

                  <tr
                    key={
                      order.order_id
                    }
                    className="
                      border-b
                      last:border-0
                      hover:bg-gray-50
                    "
                  >

                    <td
                      className="
                        px-5
                        py-3
                        font-semibold
                      "
                    >
                      #
                      {order.order_id}
                    </td>

                    <td className="px-5 py-3">

                      <p
                        className="
                          font-medium
                          text-gray-800
                        "
                      >
                        {order.name ||
                          "Customer"}
                      </p>

                      <p
                        className="
                          text-xs
                          text-gray-400
                        "
                      >
                        {order.phone ||
                          ""}
                      </p>

                    </td>

                    <td
                      className="
                        px-5
                        py-3
                        text-right
                        font-bold
                      "
                    >
                      ₹
                      {money(
                        order.net_total ||
                        order.total_amount
                      )}
                    </td>

                    <td
                      className="
                        px-5
                        py-3
                        text-center
                      "
                    >

                      <span
                        className={`
                          px-2
                          py-1
                          rounded-full
                          text-xs
                          font-semibold
                          capitalize
                          ${getOrderStatusClass(
                            order.status
                          )}
                        `}
                      >
                        {order.status ||
                          "pending"}
                      </span>

                    </td>

                    <td
                      className="
                        px-5
                        py-3
                        text-right
                        text-xs
                        text-gray-400
                      "
                    >
                      {formatDateTime(
                        order.created_at
                      )}
                    </td>

                  </tr>

                )
              )}

            </tbody>

          </table>

        </div>

        {/* MOBILE */}

        <div
          className="
            md:hidden
            divide-y
          "
        >

          {recentOrders.map(
            (order) => (

              <div
                key={
                  order.order_id
                }
                className="p-4"
              >

                <div
                  className="
                    flex
                    items-start
                    justify-between
                    gap-3
                  "
                >

                  <div>

                    <p
                      className="
                        font-bold
                        text-gray-800
                      "
                    >
                      #
                      {order.order_id}
                    </p>

                    <p
                      className="
                        text-sm
                        text-gray-600
                      "
                    >
                      {order.name ||
                        "Customer"}
                    </p>

                    <p
                      className="
                        text-xs
                        text-gray-400
                        mt-1
                      "
                    >
                      {formatDateTime(
                        order.created_at
                      )}
                    </p>

                  </div>

                  <div
                    className="
                      text-right
                    "
                  >

                    <p
                      className="
                        font-bold
                        text-blue-600
                      "
                    >
                      ₹
                      {money(
                        order.net_total ||
                        order.total_amount
                      )}
                    </p>

                    <span
                      className={`
                        text-[10px]
                        px-2
                        py-1
                        rounded-full
                        font-semibold
                        capitalize
                        ${getOrderStatusClass(
                          order.status
                        )}
                      `}
                    >
                      {order.status ||
                        "pending"}
                    </span>

                  </div>

                </div>

              </div>

            )
          )}

        </div>

        {recentOrders.length === 0 && (
          <div
            className="
              text-center
              py-10
              text-gray-400
            "
          >
            No orders found.
          </div>
        )}

      </div>

      {/* ====================================================
          RECENT VISITS
      ==================================================== */}

      <div
        className="
          bg-white
          rounded-2xl
          border
          border-gray-100
          shadow-sm
          overflow-hidden
        "
      >

        <div
          className="
            p-4
            md:p-5
            border-b
            border-gray-100
            flex
            items-center
            justify-between
          "
        >

          <div
            className="
              flex
              items-center
              gap-2
            "
          >

            <MapPin
              size={18}
              className="text-orange-500"
            />

            <h2
              className="
                font-bold
                text-gray-800
              "
            >
              Recent Customer Visits
            </h2>

          </div>

          <span
            className="
              text-xs
              text-gray-400
            "
          >
            Last 5
          </span>

        </div>

        <div className="divide-y">

          {recentVisits.map(
            (visit) => (

              <div
                key={visit.id}
                className="
                  p-4
                  md:px-5
                  hover:bg-gray-50
                "
              >

                <div
                  className="
                    flex
                    items-start
                    justify-between
                    gap-3
                  "
                >

                  <div className="min-w-0">

                    <p
                      className="
                        font-semibold
                        text-gray-800
                      "
                    >
                      {visit.customer_name ||
                        "Customer"}
                    </p>

                    <p
                      className="
                        text-xs
                        text-gray-400
                      "
                    >
                      {visit.customer_phone ||
                        ""}
                    </p>

                    {visit.purpose && (
                      <p
                        className="
                          text-xs
                          text-gray-500
                          mt-1
                        "
                      >
                        {visit.purpose}
                      </p>
                    )}

                  </div>

                  <span
                    className={`
                      shrink-0
                      px-2
                      py-1
                      rounded-full
                      text-[10px]
                      font-semibold
                      ${
                        visit.status ===
                        "completed"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }
                    `}
                  >
                    {visit.status ===
                    "completed"
                      ? "Completed"
                      : "In Progress"}
                  </span>

                </div>

                <div
                  className="
                    flex
                    flex-wrap
                    gap-4
                    mt-3
                    text-xs
                    text-gray-400
                  "
                >

                  <span
                    className="
                      flex
                      items-center
                      gap-1
                    "
                  >
                    <Clock size={12} />

                    In:{" "}
                    {formatTime(
                      visit.check_in_time
                    )}
                  </span>

                  <span
                    className="
                      flex
                      items-center
                      gap-1
                    "
                  >

                    <CheckCircle
                      size={12}
                    />

                    Out:{" "}
                    {visit.check_out_time
                      ? formatTime(
                          visit.check_out_time
                        )
                      : "—"}

                  </span>

                </div>

              </div>

            )
          )}

        </div>

        {recentVisits.length === 0 && (
          <div
            className="
              text-center
              py-10
              text-gray-400
            "
          >
            No customer visits found.
          </div>
        )}

      </div>

    </div>
  );
}